import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { errorQueue } from '../../assets/constants';
import { AnalysisDocument, AnalysisReasons, IAuthorize } from '../../interface/analysis';
import { CitiesService } from './cities.service';
import { ActionEvaluateEsocial } from '../../enum/action-evaluate-esocial';
import { mockEsocial } from '../../mock/esocial.mock';

@Injectable({
  providedIn: 'root',
})
export class AnalysisService {
  private url = environment.url;
  private citiesService: CitiesService;
  featureFlagEsocial = {
    approvedEsocialFields: false,
    overrideEsocialName: false,
    newEsocialFlow: false,
    esocialWithoutPis: false,
  };

  name$ = new BehaviorSubject('');
  documentCurrent$ = new BehaviorSubject('');
  private pis = 'candidate/pis/numero';
  esocialWithoutPis = false;

  constructor(
    private http: HttpClient,
  ) {
    this.citiesService = new CitiesService(http);
  }

  analysis: any;

  setAnalysis(analysis: any) {
    this.analysis = analysis;
  }

  getNextAnalysis(id: string): Observable<any> {
    return this.http.get<any>(`${this.url}/analysis/next/${id}`).pipe(
      map((res) => {
        sessionStorage.setItem('state', res.token)
        return({
          info: res.data,
          documents: this.setNewState(res.data),
          reasons: this.setReasons(res.data.events)
      })}),
      catchError((err) => throwError(() => err))
    );
  }


   getNextWithoutId(): Observable<any> {
    this.analysis = null;

    return this.http.get<any>(`${this.url}/analysis/next`).pipe(
      map((res) => {
        sessionStorage.setItem('state', res.token)
        return({
          info: res.data,
          documents: this.setNewState(res.data),
          reasons: this.setReasons(res.data.events)
      })}),
      catchError((err) => throwError(() => err))
    );
  }

  getAuthorize(url: string): Observable<IAuthorize> {
    if(environment.mock) {
      url = environment.cookie_url
    }
    return this.http.get<IAuthorize>(url);
  }

  setNewState(data) {
    this.getFeatureFlag();
    let listDocuments: string[] = data?.events?.analysis_module?.documents?.list;
    const events = this.setEsociaMock(data.events);
    listDocuments = this.setListDocuments(listDocuments, events);
    return Object.keys(events)
      .filter((key) => listDocuments.includes(key))
      .reduce((all, item) => {
        if (item === 'esocial_module' && events[item].fields) {
          return { ...all, [item]: this.setEsocialData(events) };
        }

        if (item === 'esocial_module' && !events[item].fields) {
          return all;
        }

        const doc = {
          ...events[item],
          selected: false,
          key: item,
          fields: {
            ...events[item].fields,
            data: this.settingScreenInfo(events, [...events[item].fields.list, ...events[item].fields.references]),
          },
          images: {
            ...events[item].images,
            data: this.settingScreenInfo(events, events[item].images.list),
          },
        };
        return { ...all, [item]: doc };
      }, {});
  }

  private setReasons(events): AnalysisReasons[] {
    const arrayReasons = Object.keys(events).filter((key: string) => key.startsWith('reason'));
    const reasons = Object.keys(events).reduce((all, item) => {
      if (arrayReasons.includes(item)) {
        all.push(events[item])
      }
      return all;
    }, []);

    return reasons;
  }

  private setEsociaMock(events): any {
    if (this.featureFlagEsocial[ActionEvaluateEsocial.EsocialWithoutPis] && !events?.esocial_module) {
      return {...events, esocial_module: mockEsocial};
    } else {
      return events;
    }
  }

  private setListDocuments(listDocuments, events): any {
    if (this.featureFlagEsocial[ActionEvaluateEsocial.EsocialWithoutPis]) {
      return  [...listDocuments, 'esocial_module'];
    } else {
      if (events?.esocial_module && events[this.pis]) {
        return [...listDocuments, 'esocial_module'];
      } else {
        return listDocuments;
      }
    }
  }

  // Function responsible for fetching the fields/images from the config file config_v4_2.json
  private settingScreenInfo(allEventSettings: Object, documentsToDisplay: Array<string>): Array<any> {
    const indexObject = 0;
    // Array with document info
    const documentsinfo = [];
    // Loop to find the document settings in the event list of config_v4_2.json
    // TODO: remove field dismissed in the future. line 99 and 100
    documentsToDisplay.forEach((documentKey) => {
      const documentFound = Object.keys(allEventSettings)
      .filter((key) => documentKey === key &&
      allEventSettings[key]?.type !== 'dismissed' &&
      allEventSettings[key]?.type !== 'none' &&
      allEventSettings[key]?.deleted !== true)
      .map((item) => {
        return ({ ...allEventSettings[item], key: item });
      })
      // the map method returns an array, as I'm not using this array I'm transforming it into an object
      if (documentFound[indexObject]) {
        documentsinfo.push(Object.assign({}, documentFound[indexObject]));
      }
    });
    // returning the documents with the field information and the values
    return documentsinfo;
  }

  private setEsocialData(events, key = 'esocial_module'): any {
		const fields = events[key].fields.reduce((acc, item) => {
			if (events[item.reference]?.deleted) {
			return acc = [...acc, {...item, value: ''}];
			}
			
			return [...acc, {...item, value: events[item.reference]?.value || ''}];			
		}, []);

    return {
      fields,
      reasons: events[key]?.reasons,
      key: key,
      step: 'esocial',
      slug: 'esocial',
      icon: events[key].status,
      status: events[key].status
    }
  }

  getCurrentAnalysis(): Observable<any> {
    return this.http.get<any>(`${this.url}/analysis/current`).pipe(
      map((res) => ({
        info: res,
        documents: this.setNewState(res),
        reasons: this.setReasons(res.events)
      }))
    );
  }

  getFeatureFlag() {
    localStorage.getItem('featureFlagEsocial') ? 
    this.featureFlagEsocial = JSON.parse(localStorage.getItem('featureFlagEsocial')) : this.featureFlagEsocial;
  }

  // getCitiesByStateInitials fetches all cities related to a state with given initials. If no state_initials are provided,
  // all available cities will be returned instead.
  getCitiesByStateInitials(state_initials: string): Observable<any> {
    return this.citiesService.getCitiesByStateInitials(state_initials);
  }
}
